import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useAuthToken } from '../../TokenContext';
import cpApi from './cpApi';

const getOrgCode = (user) => {
  const code = user?.data?.[0]?.client_code || user?.data?.[0]?.talent_code || '';
  if (code && !code.includes('#')) {
    const type = (user?.data?.[0]?.type || '').toLowerCase();
    if (type === 'stakeholder' || type === 'employer') return `CLIENT#${code}`;
    if (type === 'candidate' || type === 'talent') return `TALENT#${code}`;
  }
  return code;
};

export function useConfidenceScore() {
  const { user } = useSelector((s) => s.auth);
  const token = useAuthToken();
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await cpApi.getConfidenceScore({ org_code: getOrgCode(user) });
      setScore(res.data?.data || null);
    } catch {
      setScore(null);
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => { fetch(); }, [fetch]);
  return { score, loading, refetch: fetch };
}

export function useRisks() {
  const { user } = useSelector((s) => s.auth);
  const token = useAuthToken();
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await cpApi.getRisks({ org_code: getOrgCode(user) });
      setRisks(res.data?.data || []);
    } catch {
      setRisks([]);
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => { fetch(); }, [fetch]);
  return { risks, loading, refetch: fetch };
}

export function useSignals() {
  const { user } = useSelector((s) => s.auth);
  const token = useAuthToken();
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const orgCode = getOrgCode(user);
      if (!orgCode) {
        setSignals([]);
        return;
      }
      const res = await cpApi.getSignals({ org_code: orgCode });
      setSignals(res.data?.data || []);
    } catch {
      setSignals([]);
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => { fetch(); }, [fetch]);
  return { signals, loading, refetch: fetch };
}

export function useIntentGraph() {
  const { user } = useSelector((s) => s.auth);
  const token = useAuthToken();
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await cpApi.getIntentGraph({ org_code: getOrgCode(user) });
      setGraph(res.data?.data || null);
    } catch {
      setGraph(null);
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => { fetch(); }, [fetch]);
  return { graph, loading, refetch: fetch };
}

export function usePolicies() {
  const { user } = useSelector((s) => s.auth);
  const token = useAuthToken();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await cpApi.getPolicies({ org_code: getOrgCode(user) });
      setPolicies(res.data?.data || []);
    } catch {
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => { fetch(); }, [fetch]);
  return { policies, loading, refetch: fetch };
}

export function useAudit() {
  const { user } = useSelector((s) => s.auth);
  const token = useAuthToken();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await cpApi.getAudit({ org_code: getOrgCode(user) });
      setEvents(res.data?.data || []);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => { fetch(); }, [fetch]);
  return { events, loading, refetch: fetch };
}

export function useAutonomyLevel() {
  const { user } = useSelector((s) => s.auth);
  const token = useAuthToken();
  const [level, setLevel] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await cpApi.getAutonomyLevel({ org_code: getOrgCode(user) });
      setLevel(res.data?.data || null);
    } catch {
      setLevel(null);
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => { fetch(); }, [fetch]);
  return { level, loading, refetch: fetch };
}

export function useDashboardMetrics() {
  const { user } = useSelector((s) => s.auth);
  const token = useAuthToken();
  const [metrics, setMetrics] = useState({
    stories: { open: 0, completed: 0, unassigned: 0 },
    defects: { open: 0, completed: 0, unassigned: 0 },
    tasks: { open: 0, completed: 0 }
  });
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!token || !user?.data?.[0]) return;
    setLoading(true);
    try {
      const userType = user.data[0].type || '';
      const c_id = user.data[0].c_id;
      const client_code = user.data[0].client_code;
      const emp_id = user.data[0].emp_id;
      const stakeholder_id = user.data[0].stakeholder_id;

      // 1. URLs
      const lowType = userType.toLowerCase();
      const isCandidate = lowType === 'candidate' || lowType === 'talent';
      const isStakeholder = lowType === 'stakeholder' || lowType === 'employer';

      const storiesUrl = isCandidate ? process.env.REACT_APP_TALENT_JIRASTORIESDASHBOARDTALENT :
        (isStakeholder ? process.env.REACT_APP_STAKEHOLDER_JIRASTATSFORSTAKEHOLDER : process.env.REACT_APP_CLIENT_JIRASTATS);
      const defectsUrl = isCandidate ? process.env.REACT_APP_TALENT_JIRADEFECTSDASHBOARDTALENT : storiesUrl;

      const tasksUrl = isCandidate ? process.env.REACT_APP_TALENT_PODTASKSDASHBOARDTALENT :
        (isStakeholder ? "https://q0aippwzde.execute-api.us-east-1.amazonaws.com/prod/pod_tasks_dashboard_stakeholder" : process.env.REACT_APP_CLIENT_PODTASKSDASHBOARDMANAGER);

      const prUrl = isCandidate ? process.env.REACT_APP_TALENT_PULLREQUESTMETRICSTALENT : 
                    (isStakeholder ? process.env.REACT_APP_STAKEHOLDER_PULLREQUESTMETRICSSTAKEHOLDER : process.env.REACT_APP_CLIENT_PULLREQUESTMETRICSMANAGER);
      const approvalsUrl = process.env.REACT_APP_CLIENT_LISTAPPROVALHANDLER;

      // 2. Payloads
      const candidatePayload = { c_id, pod_id: [] };
      const stakeholderPayload = { pod_id: [] };
      const stakeholderTasksPayload = { pod_id: [], org_code: client_code }; // Keep org_code for tasks if not explicitly asked to remove
      const clientPayload = { manager_id: emp_id, pod_id: [] };

      const basePayload = isCandidate ? candidatePayload : (isStakeholder ? stakeholderPayload : clientPayload);
      const tasksPayload = isStakeholder ? stakeholderTasksPayload : basePayload;

      // 3. Fetch Data
      const authHeader = isStakeholder ? token : `Bearer ${token}`;
      const headers = { 'Content-Type': 'application/json', Authorization: authHeader };
      const isValidUrl = (url) => url && typeof url === 'string' && url.startsWith('http') && url !== 'undefined';

      let sOpen = 0, sDone = 0, sUn = 0;
      let dOpen = 0, dDone = 0, dUn = 0;
      let tOpen = 0, tDone = 0;
      let prOpen = 0, prMerged = 0;
      let aPending = 0, aApproved = 0;

      // 1. Fetch Stories & Defects
      try {
        if (!isValidUrl(storiesUrl)) throw new Error('Invalid Stories URL');
        const resStats = await window.fetch(storiesUrl, { method: 'POST', headers, body: JSON.stringify(basePayload) }).then(r => r.json());
        const bodyObj = (typeof resStats === 'string' ? JSON.parse(resStats) : resStats) || {};
        const innerBody = bodyObj.body || bodyObj;
        const data = (typeof innerBody === 'string' ? JSON.parse(innerBody) : innerBody) || {};

        const sCounts = data.total_user_story_count || data;

        sOpen = sCounts.open_user_stories ?? sCounts.open ?? sCounts.Open ?? 0;
        sDone = sCounts.completed_user_stories ?? sCounts.completed ?? sCounts.completed_stories ?? sCounts.Complete ?? 0;
        sUn = sCounts.unassigned_stories ?? sCounts.unassigned ?? sCounts.Unassigned ?? 0;

        let dData = data;
        if (storiesUrl !== defectsUrl && isValidUrl(defectsUrl)) {
          const resDefects = await window.fetch(defectsUrl, { method: 'POST', headers, body: JSON.stringify(basePayload) }).then(r => r.json());
          const dBodyObj = (typeof resDefects === 'string' ? JSON.parse(resDefects) : resDefects) || {};
          const dInner = dBodyObj.body || dBodyObj;
          dData = (typeof dInner === 'string' ? JSON.parse(dInner) : dInner) || {};
        }

        const dCounts = dData.total_user_defect_count || dData;
        dOpen = dCounts.open_defects ?? dCounts.open ?? dCounts.Open ?? 0;
        dDone = dCounts.resolved_defects ?? dCounts.resolved ?? dCounts.completed_defects ?? dCounts.Resolved ?? 0;
        dUn = dCounts.unassigned_defects ?? dCounts.unassigned ?? dCounts.Unassigned ?? 0;
      } catch (e) {
        console.error('Error fetching Jira stats:', e);
      }

      // 2. Fetch Tasks (Needs CLIENT# prefix for stakeholders)
      try {
        let tOrgCode = client_code;
        if (isStakeholder && tOrgCode && !tOrgCode.includes('#')) tOrgCode = `CLIENT#${tOrgCode}`;

        const tasksPayloadWithPrefix = isStakeholder ? { ...tasksPayload, org_code: tOrgCode } : tasksPayload;
        if (!isValidUrl(tasksUrl)) throw new Error('Invalid Tasks URL');
        const resTasks = await window.fetch(tasksUrl, { method: 'POST', headers, body: JSON.stringify(tasksPayloadWithPrefix) }).then(r => r.json());
        const tBodyObj = (typeof resTasks === 'string' ? JSON.parse(resTasks) : resTasks) || {};
        const tInner = tBodyObj.body || tBodyObj;
        const taskData = (typeof tInner === 'string' ? JSON.parse(tInner) : tInner) || [];

        if (Array.isArray(taskData)) {
          tOpen = taskData.filter(t => t.status === 'Open' || t.status === 'To Do').length;
          tDone = taskData.filter(t => t.status === 'Complete' || t.status === 'Done').length;
        } else {
          tOpen = taskData?.Open ?? taskData?.open ?? taskData?.status_counts?.Open ?? 0;
          tDone = taskData?.Complete ?? taskData?.completed ?? taskData?.status_counts?.Complete ?? 0;
        }
      } catch (e) {
        console.error('Error fetching Tasks:', e);
      }

      // 3. Fetch PRs
      try {
        if (!isValidUrl(prUrl)) throw new Error('Invalid PR URL');
        const prPayload = isCandidate ? { talent_id: user.data[0].c_id, pod_id: [] } : basePayload;
        const prHeaders = isCandidate ? { 'Content-Type': 'application/json', Authorization: token } : headers;

        const resPR = await window.fetch(prUrl, { method: 'POST', headers: prHeaders, body: JSON.stringify(prPayload) }).then(r => r.json());
        const prBodyObj = (typeof resPR === 'string' ? JSON.parse(resPR) : resPR) || {};
        const prInner = prBodyObj.body ? (typeof prBodyObj.body === 'string' ? JSON.parse(prBodyObj.body) : prBodyObj.body) : prBodyObj;
        const prData = (typeof prInner === 'string' ? JSON.parse(prInner) : prInner) || {};

        prOpen = prData.total_prs?.total_submitted_prs ?? prData.total_prs?.['Total Submitted PRs'] ?? prData.open ?? prData.open_prs ?? 0;
        prMerged = prData.total_prs?.total_Approved_prs ?? prData.total_prs?.['Total Approved PRs'] ?? prData.merged ?? prData.merged_prs ?? 0;
      } catch (e) { console.error('Error fetching PRs:', e); }

      // 4. Fetch Approvals
      try {
        if (!isValidUrl(approvalsUrl)) throw new Error('Invalid Approvals URL');
        const reqPayload = {
          env: 'prod',
          approver_id: isCandidate ? c_id : (isStakeholder ? stakeholder_id : emp_id),
          org_code: isStakeholder ? client_code : undefined,
          status: 'pending',
          limit: 10,
          offset: 0
        };
        const resAppr = await window.fetch(approvalsUrl, { method: 'POST', headers, body: JSON.stringify(reqPayload) }).then(r => r.json());
        const appBodyObj = (typeof resAppr === 'string' ? JSON.parse(resAppr) : resAppr) || {};
        const appInner = appBodyObj.body ? (typeof appBodyObj.body === 'string' ? JSON.parse(appBodyObj.body) : appBodyObj.body) : appBodyObj;

        const innerData = appInner.data || appInner;
        const approvalList = Array.isArray(innerData.approvals) ? innerData.approvals : (Array.isArray(innerData) ? innerData : []);

        aPending = innerData.count ?? innerData.pending_count ?? (Array.isArray(approvalList) ? approvalList.length : 0);
        aApproved = innerData.approved_count ?? innerData.resolved_count ?? 0;
      } catch (e) { console.error('Error fetching Approvals:', e); }

      setMetrics({
        stories: { open: Number(sOpen) || 0, completed: Number(sDone) || 0, unassigned: Number(sUn) || 0 },
        defects: { open: Number(dOpen) || 0, completed: Number(dDone) || 0, unassigned: Number(dUn) || 0 },
        tasks: { open: Number(tOpen) || 0, completed: Number(tDone) || 0 },
        pullRequests: { open: Number(prOpen) || 0, merged: Number(prMerged) || 0 },
        approvals: { pending: Number(aPending) || 0, approved: Number(aApproved) || 0 }
      });
      console.log('>>> [DASHBOARD FINAL DEBUG] Metrics set to:', { sOpen, sDone, dOpen, dDone, tOpen, tDone });
    } catch (e) {
      console.error('Failed to fetch dashboard metrics:', e);
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => { fetch(); }, [fetch]);
  return { metrics, loading, refetch: fetch };
}

export function useSprintVelocity() {
  const { user } = useSelector((s) => s.auth);
  const token = useAuthToken();
  const [velocity, setVelocity] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!token || !user?.data?.[0]) return;
    setLoading(true);
    try {
      const u = user.data[0];
      const type = (u.type || '').toLowerCase();
      const isCandidate = type === 'candidate' || type === 'talent';
      const isStakeholder = type === 'stakeholder' || type === 'employer';

      const url = isCandidate ? process.env.REACT_APP_TALENT_SPRINTVELMETRICSTALTENT :
        (isStakeholder ? process.env.REACT_APP_STAKEHOLDER_SPRINTVELMETRICSSTAKEHOLDER : process.env.REACT_APP_CLIENT_SPRINTVELOCITY);

      const payload = isCandidate ? { c_id: u.c_id, pod_id: [] } : 
                     (isStakeholder ? { pod_id: [], org_code: u.client_code } : { manager_id: u.emp_id, org_code: u.client_code, pod_id: null });

      const authHeader = isStakeholder ? token : `Bearer ${token}`;
      const res = await window.fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: authHeader },
        body: JSON.stringify(payload)
      }).then(r => r.json());

      if (res.body) {
        const data = JSON.parse(res.body.replace(/NaN/g, 'null'));
        setVelocity(data.map(d => ({ sprint: d.sprint_name, velocity: parseFloat(d.sprint_velocities) || 0 })));
      }
    } catch (e) {
      console.error(e);
      setVelocity([]);
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => { fetch(); }, [fetch]);
  return { velocity, loading, refetch: fetch };
}

export function usePRCount() {
  const { user } = useSelector((s) => s.auth);
  const token = useAuthToken();
  const [prCount, setPRCount] = useState({ submitted: 0, approved: 0 });
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!token || !user?.data?.[0]) return;
    setLoading(true);
    try {
      const u = user.data[0];
      const type = (u.type || '').toLowerCase();
      const isCandidate = type === 'candidate' || type === 'talent';
      const isStakeholder = type === 'stakeholder' || type === 'employer';

      const url = isCandidate ? process.env.REACT_APP_TALENT_PULLREQUESTMETRICSTALENT : 
                  (isStakeholder ? process.env.REACT_APP_STAKEHOLDER_PULLREQUESTMETRICSSTAKEHOLDER : process.env.REACT_APP_CLIENT_PULLREQUESTMETRICSMANAGER);
      
      const payload = isCandidate ? { c_id: u.c_id, pod_id: [] } : 
                     (isStakeholder ? { pod_id: [] } : 
                     { manager_id: u.emp_id, pod_id: [] });

      const authHeader = isStakeholder ? token : `Bearer ${token}`;
      const res = await window.fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: authHeader },
        body: JSON.stringify(payload)
      }).then(r => r.json());

      if (res.body) {
        const data = typeof res.body === 'string' ? JSON.parse(res.body) : res.body;
        if (data.total_prs) {
          setPRCount({
            submitted: data.total_prs.total_submitted_prs ?? data.total_prs['Total Submitted PRs'] ?? 0,
            approved: data.total_prs.total_Approved_prs ?? data.total_prs['Total Approved PRs'] ?? 0
          });
        }
      }
    } catch (e) {
      console.error(e);
      setPRCount({ submitted: 0, approved: 0 });
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => { fetch(); }, [fetch]);
  return { prCount, loading, refetch: fetch };
}
